import React, { useContext, useEffect, useState } from "react";
import Cookies from "universal-cookie";
import QuestContext from "../../../../components/QuestWrapper";
import { useTranslations } from "./useTranslations";
import { useFormData } from "./useFormData";
import { useAnswerManagement } from "./useAnswerManagement";
import { submitFormData, handleUserMetrics } from "./api";
import ThankYouPopup from "./ThanksPopup";
import GamifiedQuizFooter from "./GamifiedQuizFooter";
import { NavigateButton } from "./NavigateButton";
import { GamifiedQuizQuestions } from "./GamifiedQuizQuestion";
import {
  BrandTheme,
  GamifiedQuizProps,
  QuestThemeData,
  StyleConfig,
} from "../types/GamifiedQuiz.type";
import config from "../../../../config";
import General, { useGeneral } from "../../../../general";
import GamifiedQuizTaskLogo from "../../../../assets/images/GamifiedQuizTaskLogo.svg";
import { useThemeManager } from "./useThemeManager";
import CloseIcon from "../../../../assets/images/closeIcon.svg";
import { TaskLogo } from "./SVG";
import { hexToGlassColor } from "../../../../components/chatComponent/components/helperfunction";

interface ValidationState {
  [key: string]: {
    isValid: boolean;
    regexValid: boolean;
  };
}

const GamifiedQuiz: React.FC<GamifiedQuizProps> = ({
  userId,
  token,
  questId: campaignId,
  uniqueUserId,
  uniqueEmailId,
  heading,
  styleConfig,
  thanksPopUpFooter = true,
  footerText,
  footerLink,
  design = [],
  setGamifiedQuiz,
  gamifiedQuiz,
  setQuestions,
  questionsPerSection = 2,
  functionOnSubmit,
  feedbackContent,
  variation,
  isV1Api,
  Headers,
  isAccumulateAndSelf,
  singleChoiceModal,
  multiChoiceModal,
  ratingType,
  isGradientBackground,
  gradientBackgroundColor,
  entityAuthenticationToken,
}) => {
  const { apiKey, apiSecret, entityId, apiType, themeConfig, user } = useContext(
    QuestContext.Context
  );
  const cookies = new Cookies();
  const [showFooter, setShowFooter] = useState(true);
  const [sectionNo, setSectionNo] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [thanksPopup, setThanksPopup] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState(
    "Thanks for submitting your feedback with us. We appreciate your review and will assure you to surely consider them"
  );
  const [popUpHead, setPopUpHead] = useState("Feedback Submitted");

  const BACKEND_URL =
    apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
  const newId = isV1Api ? "criteriaId" : "actionId";
  const GeneralFunctions = new General("mixpanel", apiType);

  const urlParams = new URLSearchParams(window.location.search);
  const localeCode = urlParams.get("localeCode");
  const defaultEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [validationState, setValidationState] = useState<ValidationState>({});

  const [questThemeData, setQuestThemeData] = useState<QuestThemeData>({
    accentColor: "",
    theme: "",
    borderRadius: "",
    buttonColor: "",
    images: [],
  });
  const [BrandTheme, setBrandTheme] = useState<BrandTheme>({
    accentColor: "",
    background: "",
    borderRadius: "",
    buttonColor: "",
    contentColor: "",
    fontFamily: "",
    logo: "",
    primaryColor: "",
    secondaryColor: "",
    tertiaryColor: "",
    titleColor: "",
  });
  const themeManager = useThemeManager(
    BrandTheme,
    styleConfig as StyleConfig,
    themeConfig,
    questThemeData
  );

  const {
    translatedData,
    translatedHeader,
    translatedButton,
    fetchLocalesAndTranslations,
  } = useTranslations(entityId, GeneralFunctions);

  const {
    formdata,
    campaignVariationId,
    totalSectionsPerSectionQuestion,
    getQuestData,
    getQuestDataV1,
  } = useFormData(
    isV1Api,
    BACKEND_URL,
    entityId,
    campaignId,
    questionsPerSection,
    setQuestions,
    GeneralFunctions,
    localeCode,
    setQuestThemeData,
    setBrandTheme,
    setShowFooter
  );

  const {
    selectedOptions,
    answer,
    goToNextSection,
    handleCheckboxChange,
    handleRadioChange,
    handleRatingChange,
    handleUpdate,
    setGoToNextSection,
  } = useAnswerManagement(
    design,
    sectionNo,
    formdata,
    questionsPerSection,
    currentSection,
    newId
  );

  const general = useGeneral('quest')

  useEffect(() => {
    const initializeQuiz = async () => {
      if (!entityId) return;

      GeneralFunctions.fireTrackingEvent(
        "quest_gamifiedquiz_loaded",
        "gamifiedquiz"
      );

      const headers = {
        apiKey,
        apisecret: apiSecret,
        userId,
        token,
        ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
      };

      const externalUserId = cookies.get("externalUserId");
      const questUserId = cookies.get("questUserId");
      const questUserToken = cookies.get("questUserToken");

      try {
        await handleUserMetrics({
          BACKEND_URL,
          entityId,
          campaignId,
          uniqueUserId,
          uniqueEmailId,
          externalUserId,
          questUserId,
          questUserToken,
          headers,
          GeneralFunctions,
        });
        if (isV1Api) {
          const translations = await fetchLocalesAndTranslations();
          await getQuestDataV1(userId, headers, translations, variation);
        } else {
          await getQuestData(userId, headers, variation);
        }

        general.trackEvent("quest-gamifiedquiz-loaded",
          (uniqueUserId || userId || user.userId)!,
          {},
          true,
          !uniqueUserId,
          true
        );

      } catch (error) {
        console.error("Error initializing quiz:", error);
        GeneralFunctions.captureSentryException(error);
      }
    };

    initializeQuiz();
  }, [entityId]);

  const formSubmitHandler = async () => {
    GeneralFunctions.fireTrackingEvent(
      "quest_gamifiedquiz_submit_button_clicked",
      "gamifiedquiz"
    );

    try {
      const headers = {
        apikey: apiKey,
        apisecret: apiSecret,
        userId: uniqueUserId || userId,
        token,
        ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
      };

      const result = await submitFormData(
        isV1Api,
        BACKEND_URL,
        entityId,
        campaignId,
        answer,
        selectedOptions,
        campaignVariationId,
        headers,
        formdata,
        isAccumulateAndSelf,
        GeneralFunctions
      );

      functionOnSubmit?.();
      setThanksPopup(result.success);

      general.trackEvent("quest-gamifiedquiz-submission",
        (uniqueUserId || userId || user.userId)!,
        {},
        true,
        !uniqueUserId
      );

      if (!result.success) {
        setPopUpHead("Feedback Not Submitted");
        setPopUpMessage(result.error);
        setThanksPopup(true);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setPopUpHead("Feedback Not Submitted");
      setPopUpMessage("An error occurred while submitting your feedback");
      setThanksPopup(true);
    }
  };

  const validateField = (
    questionId: string,
    value: any,
    question: any,
    selectedOptions: Record<string, Array<string>>,
    answer: Record<string, string | Array<string>>
  ) => {
    let isValid = true;
    let regexValid = true;

    if (question.required) {
      switch (question.type) {
        case "USER_INPUT_MULTI_CHOICE":
          isValid = selectedOptions[questionId]?.length > 0;
          break;
        case "USER_INPUT_SINGLE_CHOICE":
          isValid = !!answer[questionId];
          break;
        case "RATING":
          isValid =
            answer[questionId] !== undefined && answer[questionId] !== "";
          break;
        case "USER_INPUT_TEXT":
        case "USER_INPUT_EMAIL":
        case "USER_INPUT_TEXTAREA":
        case "USER_INPUT_DATE":
        case "USER_INPUT_PHONE":
        case "USER_INPUT_NUMBER":
          isValid = value !== undefined && value !== "";
          break;
        default:
          isValid = !!value;
      }
    }

    if (question.type === "USER_INPUT_EMAIL" && value) {
      if (question.regexKey) {
        try {
          const regex = new RegExp(question.regexKey);
          regexValid = regex.test(value.toString());
        } catch (e) {
          regexValid = defaultEmailPattern.test(value.toString());
        }
      } else {
        regexValid = defaultEmailPattern.test(value.toString());
      }
    } else if (question.regexKey && value) {
      try {
        const regex = new RegExp(question.regexKey);
        regexValid = regex.test(value.toString());
      } catch (e) {
        regexValid = true;
      }
    }

    return { isValid, regexValid };
  };

  const updateValidationState = () => {
    if (design[sectionNo]) {
      const newValidationState: ValidationState = {};

      design[sectionNo].forEach((questionIndex) => {
        const question = formdata[questionIndex - 1];
        if (question) {
          const value = answer[question[newId]];
          const validation = validateField(
            question[newId],
            value,
            question,
            selectedOptions,
            answer
          );
          newValidationState[question[newId]] = validation;
        }
      });

      setValidationState(newValidationState);
      const isSectionValid = Object.values(newValidationState).every(
        (state) => state.isValid && state.regexValid
      );
      setGoToNextSection(isSectionValid);
    }
  };

  const validateInputRegex = (validated: boolean, actionId: string) => {
    if (!validationState[actionId]) return;

    setValidationState((prev) => ({
      ...prev,
      [actionId]: {
        ...prev[actionId],
        regexValid: validated,
      },
    }));

    const isAllValid = Object.values({
      ...validationState,
      [actionId]: {
        ...validationState[actionId],
        regexValid: validated,
      },
    }).every((state) => state.isValid && state.regexValid);

    setGoToNextSection(isAllValid);
  };

  useEffect(() => {
    updateValidationState();
  }, [answer, selectedOptions, sectionNo]);

  // console.log("formdata", formdata);

  return (
    <>
      {thanksPopup ? (
        <ThankYouPopup
          setThanksPopup={setThanksPopup}
          setGamifiedQuiz={setGamifiedQuiz}
          feedbackContent={feedbackContent}
          thanksPopUpFooter={showFooter}
          footerText={footerText}
          GeneralFunctions={GeneralFunctions}
          popUpHead={popUpHead}
          popUpMessage={popUpMessage}
          themeManager={themeManager}
        />
      ) : null}

      {gamifiedQuiz && !thanksPopup ? (
        <div className="upper-div">
          <div className="gamified-quiz"
            style={themeManager.getFormStyles()}

          >
            <div
              className="gamified-quiz-header-section"
              style={{ background: (isGradientBackground && gradientBackgroundColor) ? gradientBackgroundColor : themeManager.getContBgStyle(), maxHeight: showFooter ? 'calc(100% - 36px)' : '100%' }}
            >
              <div className="gamified-quiz-title-section">
                <div
                  className="gamified-quiz-title-head"
                  style={themeManager.getHeaderStyles().heading}
                >
                  {heading ? heading : "Choose an option"}
                </div>
              </div>
              <div className="div">
                <div className="gamified-quiz-header-task-logo-cont">
                  <TaskLogo primary={themeManager.getTaskLogoColors().primary} secondary={themeManager.getTaskLogoColors().secondary} tertiary={themeManager.getTaskLogoColors().tertiary} />
                </div>

                <div
                  className="gamified-quiz-header-form"
                  style={themeManager.getHeaderFormStyles()}
                >
                  <div className="gamified-quiz-header-form-container">
                    <div className="heading-subheading-container">
                      <div
                        className="quest-title"
                        style={themeManager.getSubHeadingStyles()}
                      >
                        {Headers?.[currentSection]?.heading ||
                          (isV1Api
                            ? translatedData.length > 0
                              ? translatedHeader
                              : ""
                            : "Fill out the Details")}

                        {isV1Api && translatedData.length > 0 && (
                          <div onClick={() => setGamifiedQuiz(false)}>
                            <img src={CloseIcon} alt="" />
                          </div>
                        )}
                      </div>

                      {Headers?.[currentSection]?.subHeading && (
                        <div
                          className="question"
                          style={themeManager.getQuestionStyles()}
                        >
                          {Headers[currentSection].subHeading}
                        </div>
                      )}
                    </div>
                    {/* <div className="gamified-quiz-criteria-div"> */}
                    {design?.length > 0 &&
                      design[sectionNo].map((question, index) => (
                        <GamifiedQuizQuestions
                          key={index}
                          question={question}
                          formdata={formdata}
                          answer={answer}
                          handleUpdate={handleUpdate}
                          styleConfig={styleConfig}
                          themeConfig={themeConfig}
                          questThemeData={questThemeData}
                          handleCheckboxChange={handleCheckboxChange}
                          handleRadioChange={handleRadioChange}
                          handleRatingChange={handleRatingChange}
                          selectedOptions={selectedOptions}
                          newId={newId}
                          themeManager={themeManager}
                          singleChoiceModal={singleChoiceModal}
                          multiChoiceModal={multiChoiceModal}
                          ratingType={ratingType}
                          validateInputRegex={validateInputRegex}
                        />
                      ))}
                  </div>
                  {/* </div> */}

                  <NavigateButton
                    design={design}
                    sectionNo={sectionNo}
                    setSectionNo={setSectionNo}
                    GeneralFunctions={GeneralFunctions}
                    setGamifiedQuiz={setGamifiedQuiz}
                    formSubmitHandler={formSubmitHandler}
                    goToNextSection={goToNextSection}
                    isV1Api={isV1Api}
                    translatedData={translatedData}
                    translatedButton={translatedButton}
                    currentSection={currentSection}
                    setCurrentSection={setCurrentSection}
                    totalSectionsPerSectionQuestion={
                      totalSectionsPerSectionQuestion
                    }
                    themeManager={themeManager}
                    formData={formdata}
                  />
                </div>
              </div>
              {showFooter && (
                <GamifiedQuizFooter
                  footerText={footerText}
                  footerLink={footerLink}
                  themeManager={themeManager}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default GamifiedQuiz;
